import React, { useState } from "react"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import ButtonIcon from "../../components/Button/Button"
import Button from "../../components/Button/Button"
import InputField from "../../components/InputField/InputField"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import {
  faBullseye,
  faCalendar,
  faChartLine,
  faClose,
  faCoins,
  faHashtag,
  faMoneyBillTrendUp,
  faSackDollar,
  faUser,
} from "@fortawesome/free-solid-svg-icons"
import { AdminGrowthInvestment } from "../../Objects/AdminGrowthInvestment/AdminGrowthInvestment"
import { SaveAdminGrowthInvestment } from "../../Objects/AdminGrowthInvestment/SaveAdminGrowthInvestment"
import { useAdminGrowthInvestment } from "../../hooks/AdminDashboard/useAdminGrowthInvestment"
import { EAdminGrowthInvestmentCategoryType } from "../../Enum/EAdminGrowthInvestmentCategoryType"
import { ECommercialOriginType } from "../../Enum/ECommercialOriginType"
import { GenericDropDown } from "../../components/Dropdown/Dropdown"
import { useTranslation } from "react-i18next"
import { getDateKeyInTimeZone } from "../../utils/timeZone"

interface AdminGrowthInvestmentPopupProps {
  title: string
  investment: AdminGrowthInvestment
  onClose: () => void
  onSuccess: () => void
}

const toInputDate = (investment: AdminGrowthInvestment) =>
  investment.id > 0 && investment.investmentDateUtc
    ? investment.investmentDateUtc.split("T")[0]
    : getDateKeyInTimeZone(new Date())

const parseNumber = (value: string) => (value.trim().length === 0 ? 0 : Number.parseFloat(value))

const categoryOptions = [
  EAdminGrowthInvestmentCategoryType.OnlineAds,
  EAdminGrowthInvestmentCategoryType.PhysicalCampaign,
  EAdminGrowthInvestmentCategoryType.People,
  EAdminGrowthInvestmentCategoryType.InfluencerOrPartner,
  EAdminGrowthInvestmentCategoryType.Tooling,
  EAdminGrowthInvestmentCategoryType.Infrastructure,
  EAdminGrowthInvestmentCategoryType.Event,
  EAdminGrowthInvestmentCategoryType.Retention,
  EAdminGrowthInvestmentCategoryType.Other,
].map((value) => ({ value: String(value), label: (value as number).toEAdminGrowthInvestmentCategoryTypeString() }))

const originOptions = Object.values(ECommercialOriginType)
  .filter((value) => typeof value === "number")
  .map((value) => ({ value: String(value), label: (value as number).toECommercialOriginTypeString() }))

export const AdminGrowthInvestmentPopup: React.FC<AdminGrowthInvestmentPopupProps> = ({
  title,
  investment,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation("admin")

  const { handleCreate, handleUpdate } = useAdminGrowthInvestment()

  const [form, setForm] = useState(new SaveAdminGrowthInvestment(investment))

  const [investmentDate, setInvestmentDate] = useState(toInputDate(investment))

  const patch = (data: Partial<SaveAdminGrowthInvestment>) =>
    setForm(new SaveAdminGrowthInvestment({ ...form, ...data }))

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const payload = new SaveAdminGrowthInvestment({
      ...form,
      investmentDateUtc: new Date(`${investmentDate}T12:00:00.000Z`).toISOString(),
    })

    const success = () => {
      onClose()
      onSuccess()
    }

    if (investment.id > 0) {
      handleUpdate(investment.id, payload, success)
      return
    }

    handleCreate(payload, success)
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={title}
              icon={faMoneyBillTrendUp}
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminGrowthInvestmentPopup.button.onClose"
                />
              }
            />

            <form onSubmit={handleSubmit} className="mt-3 space-y-3">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 p-2">
                <GenericDropDown
                  data={categoryOptions}
                  initialState={String(form.categoryType)}
                  handlerFuncion={(event) => patch({ categoryType: Number(event.target.value) })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminGrowthInvestmentPopup.select.select"
                />

                <GenericDropDown
                  data={originOptions}
                  initialState={String(form.commercialOriginType)}
                  handlerFuncion={(event) => patch({ commercialOriginType: Number(event.target.value) })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminGrowthInvestmentPopup.select.select2"
                />

                <InputField
                  type="text"
                  placeholder={t("growth_investment_title")}
                  onInvalidMessage={t("growth_investment_title_invalid")}
                  icon={faBullseye}
                  value={form.title}
                  required
                  maxLength={140}
                  onChange={(event) => patch({ title: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminGrowthInvestmentPopup.input.growthInvestmentTitle"
                />

                <InputField
                  type="date"
                  placeholder={t("growth_investment_date")}
                  onInvalidMessage={t("growth_investment_date_invalid")}
                  icon={faCalendar}
                  value={investmentDate}
                  required
                  onChange={(event) => setInvestmentDate(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminGrowthInvestmentPopup.input.growthInvestmentDate"
                />

                <InputField
                  type="number"
                  placeholder={t("growth_investment_amount")}
                  onInvalidMessage={t("growth_investment_amount_invalid")}
                  icon={faCoins}
                  value={String(form.amount || "")}
                  required
                  onChange={(event) => patch({ amount: parseNumber(event.target.value) })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminGrowthInvestmentPopup.input.growthInvestmentAmount"
                />

                <InputField
                  type="text"
                  placeholder={t("growth_investment_referral_code")}
                  icon={faHashtag}
                  value={form.commercialReferralCode ?? ""}
                  maxLength={20}
                  onChange={(event) => patch({ commercialReferralCode: event.target.value.toUpperCase() })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminGrowthInvestmentPopup.input.growthInvestmentReferralCode"
                />

                <InputField
                  type="text"
                  placeholder={t("growth_investment_campaign")}
                  icon={faChartLine}
                  value={form.campaignName ?? ""}
                  maxLength={120}
                  onChange={(event) => patch({ campaignName: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminGrowthInvestmentPopup.input.growthInvestmentCampaign"
                />

                <InputField
                  type="text"
                  placeholder={t("growth_investment_channel")}
                  icon={faBullseye}
                  value={form.channelName ?? ""}
                  maxLength={80}
                  onChange={(event) => patch({ channelName: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminGrowthInvestmentPopup.input.growthInvestmentChannel"
                />

                <InputField
                  type="text"
                  placeholder={t("growth_investment_responsible")}
                  icon={faUser}
                  value={form.responsibleName ?? ""}
                  maxLength={100}
                  onChange={(event) => patch({ responsibleName: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminGrowthInvestmentPopup.input.growthInvestmentResponsible"
                />

                <InputField
                  type="text"
                  placeholder={t("growth_investment_target")}
                  icon={faBullseye}
                  value={form.targetAudience ?? ""}
                  maxLength={140}
                  onChange={(event) => patch({ targetAudience: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminGrowthInvestmentPopup.input.growthInvestmentTarget"
                />

                <InputField
                  type="number"
                  placeholder={t("growth_investment_expected_return")}
                  icon={faSackDollar}
                  value={String(form.expectedReturnAmount || "")}
                  onChange={(event) => patch({ expectedReturnAmount: parseNumber(event.target.value) })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminGrowthInvestmentPopup.input.growthInvestmentExpectedReturn"
                />

                <InputField
                  type="number"
                  placeholder={t("growth_investment_associated_revenue")}
                  icon={faSackDollar}
                  value={String(form.associatedRevenueAmount || "")}
                  onChange={(event) => patch({ associatedRevenueAmount: parseNumber(event.target.value) })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminGrowthInvestmentPopup.input.growthInvestmentAssociatedRevenue"
                />
              </div>

              <div className="p-2">
                <InputField
                  type="text"
                  placeholder={t("growth_investment_notes")}
                  icon={faBullseye}
                  maxLength={1000}
                  value={form.notes ?? ""}
                  onChange={(event) => patch({ notes: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminGrowthInvestmentPopup.input.growthInvestmentNotes"
                />
              </div>

              <div className="flex justify-end">
                <Button
                  typeButton="submit"
                  label={t("common_save")}
                  bgColor={THEME.colors.buttonBGSaveColor}
                  iconColor={THEME.colors.buttonFGSaveColor}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminGrowthInvestmentPopup.button.commonSave"
                />
              </div>
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
